// Only include regions that are fully supported by the backend.
// Must match the countryDetails collection: GB, US, AU, CA + EUROPE group.
export const SUPPORTED_REGIONS: string[] = [
    'gb', 'us', 'au', 'ca',
    // EUROPE group (EUR)
    'fr', 'de', 'nl', 'it', 'dk', 'se', 'es', 'ie', 'be', 'at', 'pt', 'fi',
];

/**
 * Return the canonical form of an internal page path. Kibu's sitemap and
 * prerendered files use directory-style URLs, so page paths end in `/` while
 * query strings and fragments remain at the end.
 */
export const canonicalizeInternalPath = (path: string): string => {
    const [, rawPathname = '', suffix = ''] = path.match(/^([^?#]*)([?#].*)?$/) || [];
    const pathname = `/${rawPathname}`.replace(/\/{2,}/g, '/');
    const canonicalPathname = pathname === '/'
        ? '/'
        : `${pathname.replace(/\/+$/g, '')}/`;

    return `${canonicalPathname}${suffix}`;
};

// Global routes that don't require region prefixing
export const GLOBAL_ROUTES = [
    'legal',
    'privacy-policy',
    'kibu-care-terms',
    'terms-of-service',
    'delivery-and-returns',
    'blog',
    'build',
    'diy',
    'links',
    'i',
    'track',
    'tracking',
    'contact',
    'investor',
    'unsubscribe',
    'post-purchase-survey',
    'sustainability',
];

// Function to check if a path is a global route
export const isGlobalRoute = (pathname: string): boolean => {
    const firstSegment = pathname.split(/[?#]/, 1)[0].split('/').filter(Boolean)[0]?.toLowerCase();
    return GLOBAL_ROUTES.includes(firstSegment);
};

// Function to check if a region is supported
export const isRegionSupported = (region: string): boolean => {
    return SUPPORTED_REGIONS.includes(region.toLowerCase());
};

// Function to get current region from URL
export const getRegionFromUrl = (pathname: string): string | null => {
    // Don't process region for global routes
    if (isGlobalRoute(pathname)) {
        return null;
    }

    const match = pathname.match(/^\/([^/]+)/);
    if (match && isRegionSupported(match[1])) {
        return match[1].toLowerCase();
    }
    return null;
};

// Function to build region-specific URL
export const buildRegionUrl = (region: string, path: string): string => {
    const [, pathname = '', suffix = ''] = path.match(/^([^?#]*)([?#].*)?$/) || [];
    const pathParts = pathname.split('/').filter(Boolean);

    // Global routes are not region-prefixed, but still use the canonical
    // trailing-slash form.
    if (isGlobalRoute(pathname)) {
        return canonicalizeInternalPath(`/${pathParts.join('/')}${suffix}`);
    }

    const firstPart = pathParts[0];
    const hasRegionPrefix = firstPart && isRegionSupported(firstPart);
    const pathWithoutRegion = hasRegionPrefix ? pathParts.slice(1) : pathParts;
    const cleanPath = pathWithoutRegion.join('/');

    const regionalPath = cleanPath
        ? `/${region.toLowerCase()}/${cleanPath}${suffix}`
        : `/${region.toLowerCase()}${suffix}`;

    return canonicalizeInternalPath(regionalPath);
};

// Function to ensure a path has a region prefix
export const ensureRegionPrefix = (path: string, defaultRegion: string = 'gb'): string => {
    // Don't add region prefix to global routes
    if (isGlobalRoute(path)) {
        return canonicalizeInternalPath(path);
    }

    const currentRegion = getRegionFromUrl(path);
    if (!currentRegion) {
        return buildRegionUrl(defaultRegion, path);
    }
    return canonicalizeInternalPath(path);
};

// Function to preserve path when changing regions
export const changeRegionPreservePath = (currentPath: string, newRegion: string, search?: string): string => {
    // Don't add region prefix to global routes
    if (isGlobalRoute(currentPath)) {
        const canonicalPath = canonicalizeInternalPath(currentPath);
        return search ? `${canonicalPath}${search}` : canonicalPath;
    }

    // Extract path without any existing region prefix
    const pathParts = currentPath.split('/').filter(Boolean);
    const firstPart = pathParts[0];

    // Check if first part looks like a region (2-letter code, regardless of support status)
    // This includes unsupported regions like 'ch' so we can replace them correctly
    const hasRegionPrefix = firstPart && /^[a-z]{2}$/i.test(firstPart);

    // Get path without region prefix
    const pathWithoutRegion = hasRegionPrefix
        ? '/' + pathParts.slice(1).join('/')
        : currentPath;

    // Handle root path
    const cleanPath = pathWithoutRegion === '/' ? '' : pathWithoutRegion;

    // Build new path with new region
    const newPath = canonicalizeInternalPath(`/${newRegion}${cleanPath}`);

    // Add search params if provided
    return search ? `${newPath}${search}` : newPath;
};

// Function to convert ISO country code to flag emoji
export const getCountryFlag = (countryCode: string): string => {
    if (!countryCode || countryCode.length !== 2) return '';
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
}; 
